/**
 * AdminHelpRequests — Command Centre inbox for "Maya Helps" messages.
 * Teens write help messages from Help & Support; the founder replies here
 * (or from the SMS-nudge link on mobile) and the reply lands in the teen's
 * Notifications tab as a message from Maya.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Loader2, Send, CheckCircle, MessageCircle, Inbox } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminHelpRequests = () => {
  const [requests, setRequests] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiClient.request(`/api/help/admin/list?status=${filter}`);
      setRequests(d.requests || []);
      setOpenCount(d.open_count || 0);
    } catch { /* noop */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async (requestId) => {
    const message = (replyDrafts[requestId] || '').trim();
    if (!message) return;
    setSendingId(requestId);
    try {
      await apiClient.request(`/api/help/admin/${requestId}/reply`, {
        method: 'POST', body: JSON.stringify({ message }),
      });
      await load();
    } catch { /* noop */ }
    setSendingId(null);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <Inbox size={24} style={{ color: '#F5831F' }} />
          <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Maya Helps — Messages</h1>
          {openCount > 0 && (
            <span data-testid="help-open-count" className="px-2.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#DC2626' }}>{openCount} waiting</span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-6">Your reply lands in the teen&apos;s Notifications tab as a message from Maya.</p>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-5 w-fit">
          {['open', 'answered', 'all'].map(f => (
            <button key={f} data-testid={`help-filter-${f}`} onClick={() => setFilter(f)}
              className={`py-1.5 px-4 rounded-md text-sm font-medium capitalize ${filter === f ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
        ) : requests.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No {filter !== 'all' ? filter : ''} help messages</div>
        ) : (
          <div className="space-y-4">
            {requests.map(r => (
              <div key={r.request_id} data-testid={`help-request-${r.request_id}`}
                className="bg-white rounded-2xl border p-5" style={{ borderColor: r.status === 'open' ? '#FDBA74' : '#E5E7EB' }}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <MessageCircle size={16} style={{ color: '#F5831F' }} />
                    <span className="font-semibold text-gray-900">{r.user_name}</span>
                    <span className="text-xs text-gray-400">{r.user_email}</span>
                    {r.age_band === 'teen' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#7B4FD815', color: '#7B4FD8' }}>Teen</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {r.status === 'answered' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600"><CheckCircle size={13} /> Message answered</span>
                    ) : (
                      <span className="text-xs font-semibold" style={{ color: '#EA580C' }}>Waiting</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-800 bg-gray-50 rounded-xl p-3 mb-3 whitespace-pre-wrap">{r.message}</p>
                {r.status === 'answered' ? (
                  <div className="text-sm text-gray-700 bg-green-50 rounded-xl p-3 border border-green-100">
                    <span className="text-xs font-bold text-green-700 block mb-1">Maya replied · {(r.replied_at || '').slice(0, 16).replace('T', ' ')}</span>
                    <span className="whitespace-pre-wrap">{r.reply}</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      rows={2}
                      value={replyDrafts[r.request_id] || ''}
                      onChange={e => setReplyDrafts(d => ({ ...d, [r.request_id]: e.target.value }))}
                      placeholder="Write your reply — it lands in their Notifications as Maya…"
                      data-testid={`help-reply-input-${r.request_id}`}
                      className="flex-1 border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                    />
                    <button
                      onClick={() => sendReply(r.request_id)}
                      disabled={!(replyDrafts[r.request_id] || '').trim() || sendingId === r.request_id}
                      data-testid={`help-reply-send-${r.request_id}`}
                      className="px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
                      style={{ backgroundColor: '#F5831F' }}
                    >
                      {sendingId === r.request_id ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      Reply
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHelpRequests;
